import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const experiences = [
  {
    role: "Founder & CTO",
    company: "SooperMetrix",
    period: "2024 – Present",
    desc: [
      "Building a 20+ product SaaS ecosystem spanning HealthTech, Commerce AI, DevOps orchestration, and Conversational AI — architected end-to-end using React/Next.js, Python/FastAPI, Node.js, and LLM integrations.",
      "Designed and shipped production-grade platforms including an AI-powered enterprise wellbeing OS, multi-channel catalog syndication engine, and text-to-SQL BI analytics tool.",
      "Leading product strategy, architecture, and hands-on development across healthcare, e-commerce, AI/ML, and platform engineering verticals."
    ]
  },
  {
    role: "CTO — Platform Modernization & AI",
    company: "ETV Bharat",
    period: "2023 – 2024",
    desc: [
      "Led AI-augmented development workflows for a 30+ engineer team serving 50M+ users, implementing LLM-assisted code reviews and automated test generation.",
      "Drove GenAI integration into platform modernization, deploying LLM-powered content workflows, intelligent search, and AI-assisted editorial tooling.",
      "Architected cloud-native platform evolution with microservices, API-first design, observability, and CI/CD."
    ]
  },
  {
    role: "Technical Product Manager / Enterprise Architect",
    company: "Tech Mahindra",
    period: "2022 – 2023",
    desc: [
      "Architected Safana, a GenAI-enabled concierge platform with LLM-powered conversational workflows and agentic task routing across 15+ enterprise services.",
      "Led a 25-member cross-functional team in designing intelligent automation patterns and AI-first solution architecture for enterprise transformation programs."
    ]
  },
  {
    role: "CTO (Global) & Chief Growth Officer (India)",
    company: "Docty Healthcare",
    period: "2020 – 2022",
    desc: [
      "Owned product engineering and multi-geo healthcare platform delivery for 500K+ patients with 40+ engineers, architecting telemedicine systems at 99.5% availability.",
      "Built scalable platforms spanning consultations, appointments, and operational automation with API-first architecture, microservices, and data-driven decision systems."
    ]
  },
  {
    role: "Group CTO & Media Head",
    company: "ApolloLife, FHPL & BPositive Magazine",
    period: "2018 – 2020",
    desc: [
      "Drove platform consolidation from 4 systems to 1 unified architecture across wellness, subscriptions, and digital engagement — achieving 50% performance improvement.",
      "Led architecture, delivery management, and platform evolution across content, commerce, and digital-experience journeys."
    ]
  },
  {
    role: "Director of Technology",
    company: "Patient Focus Pvt. Ltd.",
    period: "2016 – 2018",
    desc: [
      "Led healthcare technology modernization across 50+ hospitals, driving API-first design, modular services, and intelligent dashboards — expanding platform coverage from 40% to 80%."
    ]
  },
  {
    role: "Technical Product Manager",
    company: "Mantena Consultech Pvt. Ltd.",
    period: "2014 – 2016",
    desc: [
      "Architected and delivered Mobizz.com, a hybrid social + business platform with real-time engagement, scalable backend services, and full-stack product engineering ownership."
    ]
  },
  {
    role: "Technical Product Manager / Architect",
    company: "Ed-Ventures E-Learning Pvt. Ltd.",
    period: "2010 – 2014",
    desc: [
      "Delivered learning and engagement platforms for 100K+ students with a 12-engineer team, achieving 3x faster content delivery through scalable architecture and caching improvements."
    ]
  },
  {
    role: "Consolidated Early Career Experience",
    company: "Foundational Tech Hubs",
    period: "2000 – 2010",
    desc: [
      "Spearheaded early wave web and mobile engineering (J2ME, .NET Compact Framework, PHP, Java), physically laying the foundational software architecture for multiple rapidly evolving digital startups.",
      "Mastered full-cycle monolithic product development alongside aggressive algorithmic prototyping and hands-on enterprise-grade database structuring."
    ]
  }
];

const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section id="experience" ref={ref} className="container" style={{ padding: '50px 0' }}>
      <motion.h2 
        initial={{ opacity: 0, y: 20 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        className="section-title"
      >
        My <span className="text-gradient">Journey</span>
      </motion.h2>

      <div style={{ position: 'relative', paddingLeft: '2rem', borderLeft: '2px solid rgba(255,255,255,0.1)' }}>
        {experiences.map((exp, index) => (
          <motion.div 
            key={index}
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ delay: index * 0.15, duration: 0.5 }}
            style={{ marginBottom: '3rem', position: 'relative' }}
          >
            {/* Timeline dot */}
            <div style={{
              position: 'absolute', left: '-35px', top: '10px',
              width: '20px', height: '20px', borderRadius: '50%',
              background: 'var(--bg-primary)', border: '4px solid var(--accent-purple)',
              boxShadow: '0 0 10px var(--accent-purple)'
            }}></div>

            <motion.div 
              whileHover={{ scale: 1.02, y: -5 }}
              className="glass-panel" 
              style={{ padding: '2rem' }}
            >
              <h3 style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: '0.2rem' }}>{exp.role}</h3>
              <h4 className="text-gradient" style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>{exp.company} <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: '0.5rem' }}>| {exp.period}</span></h4>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: '1rem' }}>
                {exp.desc.map((bullet, i) => (
                  <div key={i} style={{ marginBottom: '0.6rem', display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--accent-cyan)', marginRight: '8px', flexShrink: 0 }}>•</span>
                    <span>{bullet}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default Experience;
